/**
 * @license
 * Copyright 2016 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Overlay } from '../overlay';
import { LAYER_CONTROLS, SKELETON_RENDERING_SHADER_CONTROL_TOOL_ID } from '../segmentation_user_layer';
import { DependentViewWidget } from '../widget/dependent_view_widget';
import { makeHelpButton } from '../widget/help_button';
import { addLayerControlToOptionsTab } from '../widget/layer_control';
import { LinkedLayerGroupWidget } from '../widget/linked_layer';
import { makeMaximizeButton } from '../widget/maximize_button';
import { ShaderCodeWidget } from '../widget/shader_code_widget';
import { ShaderControls } from '../widget/shader_controls';
import { Tab } from '../widget/tab_view';
function makeSkeletonShaderCodeWidget(layer) {
    return new ShaderCodeWidget({
        fragmentMain: layer.displayState.skeletonRenderingOptions.shader,
        shaderError: layer.displayState.shaderError,
        shaderControlState: layer.displayState.skeletonRenderingOptions.shaderControlState
    });
}
export class DisplayOptionsTab extends Tab {
    constructor(layer) {
        super();
        this.layer = layer;
        const element = this.element;

        element.classList.add('neuroglancer-segmentation-rendering-tab');
        // Linked segmentation control
        {
            const widget = this.registerDisposer(new LinkedLayerGroupWidget(layer.displayState.linkedSegmentationGroup));
            widget.label.textContent = 'Linked to: ';
            element.appendChild(widget.element);
        }
        // Linked segmentation control
        {
            const widget = this.registerDisposer(new LinkedLayerGroupWidget(layer.displayState.linkedSegmentationColorGroup));
            widget.label.textContent = 'Colors linked to: ';
            element.appendChild(widget.element);
        }
        for (const control of LAYER_CONTROLS) {
            element.appendChild(addLayerControlToOptionsTab(this, layer, this.visibility, control));
        }
        const skeletonControls = this.registerDisposer(new DependentViewWidget(layer.hasSkeletonsLayer, (hasSkeletonsLayer, parent, refCounted) => {
            if (!hasSkeletonsLayer) return;
            let topRow = document.createElement('div');
            topRow.className = 'neuroglancer-segmentation-dropdown-skeleton-shader-header';
            let label = document.createElement('div');
            label.style.flex = '1';
            label.textContent = 'Skeleton shader:';
            topRow.appendChild(label);
            topRow.appendChild(makeMaximizeButton({
                title: 'Show larger editor view',
                onClick: () => {
                    new ShaderCodeOverlay(this.layer);
                }
            }));
            topRow.appendChild(makeHelpButton({
                title: 'Documentation on skeleton rendering',
                href: 'https://github.com/google/neuroglancer/blob/master/src/neuroglancer/sliceview/image_layer_rendering.md'
            }));
            parent.appendChild(topRow);
            const codeWidget = refCounted.registerDisposer(makeSkeletonShaderCodeWidget(this.layer));
            parent.appendChild(codeWidget.element);
            parent.appendChild(refCounted.registerDisposer(new ShaderControls(layer.displayState.skeletonRenderingOptions.shaderControlState, this.layer.manager.root.display, this.layer, {
                visibility: this.visibility,
                toolId: SKELETON_RENDERING_SHADER_CONTROL_TOOL_ID
            })).element);
            codeWidget.textEditor.refresh();
        }, this.visibility));
        element.appendChild(skeletonControls.element);
    }
}
class ShaderCodeOverlay extends Overlay {
    constructor(layer) {
        super();
        this.layer = layer;
        this.codeWidget = this.registerDisposer(makeSkeletonShaderCodeWidget(this.layer));
        this.content.classList.add('neuroglancer-segmentation-layer-skeleton-shader-overlay');
        this.content.appendChild(this.codeWidget.element);
        this.codeWidget.textEditor.refresh();
    }
}
//# sourceMappingURL=segmentation_display_options_tab.js.map